import React, { useState, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { doc, updateDoc } from 'firebase/firestore';
import { db, auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import RateLimiter from '../utils/rateLimiter';

function NoteShareModal({ isOpen, note, onClose }) {
  const { t } = useTranslation();
  const [recipientEmail, setRecipientEmail] = useState('');
  const [comment, setComment] = useState('');
  const [includeRecording, setIncludeRecording] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  
  // Security: Rate limiter - 10 shares per hour
  const shareRateLimiter = useMemo(
    () => new RateLimiter('shareNote', 10, 60 * 60 * 1000),
    []
  );

  if (!isOpen || !note) return null;

  const handleShare = async (e) => {
    e.preventDefault();
    
    if (!recipientEmail.trim()) {
      toast.error(t('errors.pleaseEnterRecipientEmail'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      toast.error(t('errors.pleaseEnterValidEmailAddress'));
      return;
    }

    // Security: Check rate limit before proceeding
    if (!shareRateLimiter.canProceed()) {
      const statusMessage = shareRateLimiter.getStatusMessage();
      toast.error(`${t('errors.rateLimitExceeded')}: ${statusMessage}`);
      return;
    }

    setIsSharing(true);

    try {
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // First, update the note's sharedIncludeRecording field
      const noteRef = doc(db, 'notes', note.id);
      await updateDoc(noteRef, {
        sharedIncludeRecording: includeRecording
      });

      // Call Firebase Function to share the note (like iOS implementation)
      const shareNote = httpsCallable(functions, 'shareNote');
      const result = await shareNote({
        docId: note.id,
        emailToSend: recipientEmail.trim(),
        emailToSendName: '', // We don't ask for name, just email
        fcmToken: '', // Web doesn't use FCM tokens
        sharedIncludeRecording: includeRecording,
        comment: comment.trim()
      });

      console.log('Share result:', result.data);

      // Security: Record successful share for rate limiting
      shareRateLimiter.recordCall();

      toast.success(t('toasts.noteSharedSuccessfully'));
      
      // Reset form
      setRecipientEmail('');
      setComment('');
      setIncludeRecording(false);
      onClose();
    } catch (error) {
      console.error('Error sharing note:', error);
      
      // Handle specific Firebase Function errors
      if (error.code === 'functions/unauthenticated') {
        toast.error(t('errors.youMustBeLoggedIn'));
      } else if (error.message) {
        toast.error(`${t('errors.failedToShare')} ${error.message}`);
      } else {
        toast.error(t('errors.failedToShareNote'));
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold mb-4 text-gray-900">{t('modals.shareNote')}</h2>
        
        {/* Note preview */}
        <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            {note.emoji && <span className="text-2xl">{note.emoji}</span>}
            <h3 className="font-semibold text-gray-900 truncate">{note.title}</h3>
          </div>
          {note.summary_short && (
            <p className="text-sm text-gray-600 line-clamp-2">{note.summary_short}</p>
          )}
        </div>

        <form onSubmit={handleShare}>
          {/* Recipient Email */}
          <div className="mb-4">
            <label htmlFor="recipientEmail" className="block text-sm font-medium text-gray-700 mb-2">
              {t('modals.recipientEmail')}
            </label>
            <input
              type="email"
              id="recipientEmail"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('modals.emailPlaceholder')}
              required
            />
          </div>

          {/* Comment */}
          <div className="mb-4">
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
              {t('modals.addCommentOptional')}
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder={t('modals.addCommentPlaceholder')}
            />
          </div>

          {/* Include Recording Checkbox */}
          {note.recordingURL && (
            <div className="mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeRecording}
                  onChange={(e) => setIncludeRecording(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{t('modals.includeAudioRecording')}</span>
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              disabled={isSharing}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-white bg-[#F07807] rounded-md hover:bg-[#d66a05] transition-colors disabled:bg-orange-400"
              disabled={isSharing}
            >
              {isSharing ? t('modals.sharing') : t('modals.shareNoteButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

NoteShareModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  note: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default NoteShareModal;

